import { useCallback, useEffect, useRef, useState } from 'react';
import { COLORS } from 'shared/styles/variables';

import Map, { MapMarker } from 'dashboard/components/Map';
import DrawingControl from 'dashboard/components/Map/DrawingControl';
import { MapProps } from 'dashboard/components/Map/Map';
import Portal from 'dashboard/components/Map/Portal';
import { Place } from 'lib/interfaces/place';

interface CircleDrawableMapProps {
  /**
   * Callback to perform any actions when the drawing button is toggled on
   */
  handleDrawCircleStart: () => void;
  /**
   * Callback to perform any actions when the drawing button is toggled off
   */
  handleDrawCircleCancel: () => void;
  /**
   * Callback to perform some action on drawing completion
   */
  handleDrawCircleComplete: (place: Place) => void;
}

const CircleDrawableMap = ({
  handleDrawCircleStart,
  handleDrawCircleCancel,
  handleDrawCircleComplete,
  ...mapProps
}: MapProps & CircleDrawableMapProps) => {
  const [apiLoaded, setApiLoaded] = useState(false);
  const drawingControlRef = useRef(null);

  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [map, updateMap] = useState<google.maps.Map>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager>(null);
  const pendingCircleRef = useRef<google.maps.Circle>(null);
  const mapListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const renderTooltipWithBoundsCheck = useCallback(
    (marker: MapMarker) => {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      if (map?.getBounds().contains({ lat: marker.lat, lng: marker.lng })) {
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        return mapProps.renderTooltip(marker);
      }
    },
    [map, mapProps],
  );

  const setPendingCircle = (circle: google.maps.Circle) => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    pendingCircleRef.current = circle;
  };

  const clearPendingCircle = () => {
    if (pendingCircleRef.current) {
      pendingCircleRef.current.setMap(null);
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setPendingCircle(null);
    }
  };

  const startDrawCircle = (map: google.maps.Map) => {
    handleDrawCircleStart();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    drawingManagerRef.current.setMap(map);
  };

  const cancelDrawCircle = () => {
    handleDrawCircleCancel();

    if (pendingCircleRef.current) {
      pendingCircleRef.current.setMap(null);
    }
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    drawingManagerRef.current.setMap(null);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    setPendingCircle(null);
  };

  const completeDrawCircle = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    drawingManagerRef.current.setMap(null);

    if (pendingCircleRef.current) {
      const center = pendingCircleRef.current.getCenter();
      if (center != null) {
        handleDrawCircleComplete({
          lat: center.lat(),
          lng: center.lng(),
          radiusMeters: pendingCircleRef.current.getRadius(),
        });
      }
      clearPendingCircle();
    } else {
      handleDrawCircleCancel();
    }
  };

  const handleApiLoaded = (map: google.maps.Map, maps: any) => {
    updateMap(map);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    drawingManagerRef.current = new maps.drawing.DrawingManager({
      drawingMode: maps.drawing.OverlayType.CIRCLE,
      drawingControl: false,
      circleOptions: {
        fillColor: COLORS.indigo100,
        fillOpacity: 0.4,
        strokeColor: COLORS.indigo100,
        strokeWeight: 2,
        editable: true,
        zIndex: 1,
      },
    });

    mapListenersRef.current.push(
      google.maps.event.addListener(
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        drawingManagerRef.current,
        'circlecomplete',
        (circle: google.maps.Circle) => {
          if (pendingCircleRef.current) {
            pendingCircleRef.current.setMap(null);
          }
          setPendingCircle(circle);
          /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
          drawingManagerRef.current.setMap(null);

          mapListenersRef.current.push(
            google.maps.event.addListener(circle, 'radius_changed', () => {
              setPendingCircle(circle);
            }),
          );

          mapListenersRef.current.push(
            google.maps.event.addListener(circle, 'center_changed', () => {
              setPendingCircle(circle);
            }),
          );
        },
      ),
    );

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    drawingControlRef.current = document.createElement('div');
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    map.controls[maps.ControlPosition.TOP_RIGHT].push(drawingControlRef.current);

    if (mapProps.handleGoogleApiLoaded) {
      mapProps.handleGoogleApiLoaded(map, maps);
    }
    setApiLoaded(true);
  };

  useEffect(() => {
    return () => {
      mapListenersRef.current.forEach((listener) => {
        google.maps.event.removeListener(listener);
      });
    };
  }, []);

  return (
    <>
      {/* We must wait till the API is finished loading so the target container div
      exists on the DOM. The Portal is to make DrawingControl aware of the React
      DOM hierarchy and its changes */}
      {apiLoaded && (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <Portal target={drawingControlRef.current}>
          <DrawingControl
            handleDrawingStart={() => startDrawCircle(map)}
            handleDrawingComplete={() => completeDrawCircle()}
            handleDrawingCancel={() => cancelDrawCircle()}
          />
        </Portal>
      )}
      {/* MUST use spread operator before assigning handleGoogleApiLoaded or else
      It will be overwritten by what is passed in the prop instead of chaining */}
      <Map
        {...mapProps}
        handleGoogleApiLoaded={handleApiLoaded}
        map={map}
        renderTooltip={renderTooltipWithBoundsCheck}
      />
    </>
  );
};

export default CircleDrawableMap;
